/**
 * LanguageContext - Global language state for UI translations
 *
 * Features:
 * - UI language toggle (English/Hebrew)
 * - Translation function t(key)
 * - RTL/LTR direction management
 * - Persists to localStorage
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

export type Language = 'English' | 'Hebrew';

interface LanguageContextType {
  uiLanguage: Language;
  setUiLanguage: (lang: Language) => void;
  t: (key: string) => string;
  isRTL: boolean;
}

// Translation dictionaries
const translations: Record<Language, Record<string, string>> = {
  English: {
    // Navigation
    'nav.agent': 'AGENT',
    'nav.pdfChat': 'PDF AI CHAT',
    'nav.replayCenter': 'REPLAY CENTER',
    'nav.documents': 'DOCUMENTS',
    'nav.agentCenter': 'AGENT CENTER',
    'nav.portalMonitor': 'PORTAL MONITOR',
    'nav.dataQuality': 'DATA QUALITY',
    'nav.clientManagement': 'CLIENT MANAGEMENT',
    'nav.home': 'Home',
    'nav.research': 'Research Hub',
    'nav.portfolio': 'Portfolio',
    'nav.chat': 'Chat',
    'nav.settings': 'Settings',
    'nav.admin': 'Admin',
    'nav.ingestion': 'Ingestion',
    'nav.analysis': 'Analysis',
    'nav.companies': 'Companies',
    'nav.analysts': 'Analysts',
    'nav.sectors': 'Sectors',

    // Chat
    'chat.placeholder': 'Ask a question about your documents...',
    'chat.send': 'Send',
    'chat.newChat': 'New Chat',
    'chat.conversations': 'Conversations',
    'chat.noConversations': 'No conversations yet',
    'chat.thinking': 'Thinking...',
    'chat.askQuestion': 'Ask a question...',
    'chat.clearHistory': 'Clear History',
    'chat.exportChat': 'Export Chat',

    // Sources
    'sources.title': 'Sources',
    'sources.noSources': 'No sources available',
    'sources.viewDocument': 'View Document',
    'sources.page': 'Page',
    'sources.relevance': 'Relevance',

    // Common
    'common.loading': 'Loading...',
    'common.error': 'Error',
    'common.retry': 'Retry',
    'common.cancel': 'Cancel',
    'common.save': 'Save',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.close': 'Close',
    'common.search': 'Search',
    'common.filter': 'Filter',
    'common.export': 'Export',
    'common.copy': 'Copy',
    'common.copied': 'Copied!',
    'common.download': 'Download',
    'common.upload': 'Upload',
    'common.refresh': 'Refresh',
    'common.back': 'Back',
    'common.next': 'Next',
    'common.previous': 'Previous',
    'common.submit': 'Submit',
    'common.confirm': 'Confirm',
    'common.yes': 'Yes',
    'common.no': 'No',
    'common.all': 'All',
    'common.none': 'None',
    'common.select': 'Select',
    'common.clear': 'Clear',
    'common.reset': 'Reset',
    'common.apply': 'Apply',
    'common.noResults': 'No results found',
    'common.success': 'Success!',
    'common.failed': 'Failed',
    'common.pending': 'Pending',
    'common.processing': 'Processing...',
    'common.complete': 'Complete',
    'common.view': 'View',
    'common.hide': 'Hide',
    'common.show': 'Show',
    'common.more': 'More',
    'common.less': 'Less',

    // User
    'user.profile': 'Profile',
    'user.logout': 'Logout',
    'user.settings': 'Settings',
    'user.login': 'Login',
    'user.register': 'Register',
    'user.email': 'Email',
    'user.password': 'Password',
    'user.name': 'Name',

    // Documents
    'documents.title': 'Documents',
    'documents.upload': 'Upload Document',
    'documents.noDocuments': 'No documents found',
    'documents.searchPlaceholder': 'Search documents...',
    'documents.recentDocuments': 'Recent Documents',
    'documents.allDocuments': 'All Documents',
    'documents.viewDocument': 'View Document',
    'documents.deleteDocument': 'Delete Document',
    'documents.downloadPdf': 'Download PDF',

    // Document Viewer
    'viewer.zoomIn': 'Zoom In',
    'viewer.zoomOut': 'Zoom Out',
    'viewer.fitWidth': 'Fit Width',
    'viewer.fitPage': 'Fit Page',
    'viewer.actualSize': 'Actual Size',
    'viewer.download': 'Download PDF',
    'viewer.fullscreen': 'Fullscreen',
    'viewer.exitFullscreen': 'Exit Fullscreen',
    'viewer.askAI': 'Ask AI',
    'viewer.copyText': 'Copy Text',
    'viewer.copyImage': 'Copy Image',
    'viewer.exportExcel': 'Export to Excel',
    'viewer.searchInDocument': 'Search in document...',
    'viewer.pageOf': 'of',
    'viewer.gridView': 'Grid View',
    'viewer.liquidView': 'Liquid View',
    'viewer.blockInspector': 'Block Inspector',
    'viewer.clickToInspect': 'Click on a block to inspect...',
    'viewer.tableExported': 'Table exported to Excel',
    'viewer.textCopied': 'Text copied to clipboard',
    'viewer.imageCopied': 'Image copied to clipboard',
    'viewer.imageDownloaded': 'Image downloaded',

    // Research Hub
    'research.title': 'Research Hub',
    'research.latestReports': 'Latest Reports',
    'research.filterBySector': 'Filter by Sector',
    'research.filterByCompany': 'Filter by Company',
    'research.filterByAnalyst': 'Filter by Analyst',
    'research.filterByDate': 'Filter by Date',
    'research.sortBy': 'Sort by',
    'research.dateNewest': 'Date (Newest)',
    'research.dateOldest': 'Date (Oldest)',
    'research.relevance': 'Relevance',
    'research.companyAZ': 'Company (A-Z)',
    'research.askAboutResearch': 'Ask about research...',
    'research.viewAll': 'View All',

    // Portfolio
    'portfolio.title': 'Portfolio',
    'portfolio.holdings': 'Holdings',
    'portfolio.watchlist': 'Watchlist',
    'portfolio.performance': 'Performance',
    'portfolio.addHolding': 'Add Holding',
    'portfolio.removeHolding': 'Remove Holding',
    'portfolio.totalValue': 'Total Value',
    'portfolio.dayChange': 'Day Change',
    'portfolio.allocation': 'Allocation',

    // Table Headers
    'table.company': 'Company',
    'table.ticker': 'Ticker',
    'table.sector': 'Sector',
    'table.date': 'Date',
    'table.analyst': 'Analyst',
    'table.rating': 'Rating',
    'table.price': 'Price',
    'table.target': 'Target',
    'table.change': 'Change',
    'table.volume': 'Volume',
    'table.marketCap': 'Market Cap',
    'table.peRatio': 'P/E Ratio',
    'table.dividend': 'Dividend',
    'table.title': 'Title',
    'table.type': 'Type',
    'table.status': 'Status',
    'table.actions': 'Actions',
    'table.source': 'Source',
    'table.pages': 'Pages',

    // Settings
    'settings.title': 'Settings',
    'settings.language': 'Language',
    'settings.theme': 'Theme',
    'settings.darkMode': 'Dark Mode',
    'settings.lightMode': 'Light Mode',
    'settings.notifications': 'Notifications',
    'settings.emailNotifications': 'Email Notifications',
    'settings.pushNotifications': 'Push Notifications',
    'settings.privacy': 'Privacy',
    'settings.account': 'Account',
    'settings.changePassword': 'Change Password',
    'settings.deleteAccount': 'Delete Account',
    'settings.preferences': 'Preferences',
    'settings.display': 'Display',
    'settings.general': 'General',

    // Admin
    'admin.title': 'Admin Panel',
    'admin.users': 'Users',
    'admin.documents': 'Documents',
    'admin.analytics': 'Analytics',
    'admin.systemStatus': 'System Status',
    'admin.logs': 'Logs',

    // Ingestion
    'ingestion.title': 'Document Ingestion',
    'ingestion.uploadPdf': 'Upload PDF',
    'ingestion.processingStatus': 'Processing Status',
    'ingestion.queued': 'Queued',
    'ingestion.processing': 'Processing',
    'ingestion.completed': 'Completed',
    'ingestion.failed': 'Failed',
    'ingestion.reprocess': 'Reprocess',
    'ingestion.batchUpload': 'Batch Upload',

    // Actions
    'action.runScraper': 'Run Scraper',
    'action.ask': 'Ask',
    'action.submit': 'Submit',
    'action.viewDetails': 'View Details',
    'action.openInNewTab': 'Open in New Tab',
    'action.share': 'Share',
    'action.print': 'Print',
    'action.bookmark': 'Bookmark',
    'action.addToWatchlist': 'Add to Watchlist',
    'action.removeFromWatchlist': 'Remove from Watchlist',

    // Messages
    'msg.welcome': 'Welcome',
    'msg.confirmDelete': 'Are you sure you want to delete this?',
    'msg.unsavedChanges': 'You have unsaved changes. Are you sure you want to leave?',
    'msg.sessionExpired': 'Your session has expired. Please login again.',
    'msg.networkError': 'Network error. Please check your connection.',
    'msg.serverError': 'Server error. Please try again later.',
    'msg.unauthorized': 'You are not authorized to perform this action.',
    'msg.notFound': 'The requested item was not found.',
    'msg.invalidInput': 'Please check your input and try again.',
    'msg.saveSuccess': 'Changes saved successfully.',
    'msg.deleteSuccess': 'Item deleted successfully.',
    'msg.uploadSuccess': 'File uploaded successfully.',
    'msg.exportSuccess': 'Export completed successfully.',

    // Dates
    'date.today': 'Today',
    'date.yesterday': 'Yesterday',
    'date.thisWeek': 'This Week',
    'date.thisMonth': 'This Month',
    'date.thisYear': 'This Year',
    'date.custom': 'Custom Range',

    // Company Page
    'company.currentRating': 'Current Rating',
    'company.priceTarget': 'Price Target',
    'company.currentPrice': 'Current Price',
    'company.upside': 'Upside',
    'company.asOf': 'As of',
    'company.noRating': 'No rating available',
    'company.coveringAnalysts': 'Covering Analysts',
    'company.noAnalysts': 'No analyst coverage found',
    'company.valuation': 'Valuation Methodology',
    'company.ratingHistory': 'Rating History',
    'company.prevRating': 'Prev Rating',
    'company.prevTarget': 'Prev Target',
    'company.recentDocuments': 'Recent Documents',
    'company.topics': 'Key Topics',

    // AI Chat specific
    'ai.askAnything': 'Ask anything about your documents',
    'ai.generating': 'Generating response...',
    'ai.sources': 'Sources',
    'ai.confidence': 'Confidence',
    'ai.copyResponse': 'Copy Response',
    'ai.regenerate': 'Regenerate',
    'ai.feedback': 'Was this helpful?',
    'ai.thumbsUp': 'Helpful',
    'ai.thumbsDown': 'Not Helpful',

    // Progress Steps
    'progress.understanding': 'Understanding',
    'progress.routing': 'Finding docs',
    'progress.retrieving': 'Reading blocks',
    'progress.generating': 'Generating',

    // Source Grouping
    'sources.blocks': 'blocks',
    'sources.docs': 'docs',
    'sources.jumpToCitation': 'Jump to Citation',

    // Thinking Trail
    'thinking.researchContext': 'Research ctx',
    'thinking.plan': 'Plan',
    'thinking.evidence': 'Evidence',
    'thinking.themes': 'Themes',
    'thinking.cost': 'Cost',

    // Changes View
    'companies.gridView': 'Grid',
    'companies.changesView': 'Changes',
    'companies.upgrades': 'Upgrades',
    'companies.downgrades': 'Downgrades',
    'companies.initiations': 'Initiations',
    'companies.noChanges': 'No rating changes found',

    // Research Hub
    'research.timeRange': 'Time Range',
    'research.contentType': 'Content Type',
    'research.askSarah': 'Ask Sarah instead',
    'research.noMatch': 'No documents match your filters',

    // Viewer TOC
    'viewer.toc': 'Table of Contents',
    'viewer.noToc': 'No table of contents available',

    // Summary Slider
    'summary.brief': 'Brief',
    'summary.standard': 'Standard',
    'summary.detailed': 'Detailed',
    'summary.title': 'Summary',

    // Compare Page
    'compare.title': 'Compare Documents',
    'compare.needTwo': 'Compare requires two documents',
    'compare.withPrevious': 'Compare with previous',
  },
  Hebrew: {
    // Navigation
    'nav.agent': 'סוכן',
    'nav.pdfChat': 'צאט AI',
    'nav.replayCenter': 'מרכז שידורים',
    'nav.documents': 'מסמכים',
    'nav.agentCenter': 'מרכז סוכנים',
    'nav.portalMonitor': 'מוניטור פורטל',
    'nav.dataQuality': 'איכות נתונים',
    'nav.clientManagement': 'ניהול לקוחות',
    'nav.home': 'בית',
    'nav.research': 'מרכז מחקר',
    'nav.portfolio': 'תיק השקעות',
    'nav.chat': 'צאט',
    'nav.settings': 'הגדרות',
    'nav.admin': 'ניהול',
    'nav.ingestion': 'קליטה',
    'nav.analysis': 'ניתוח',
    'nav.companies': 'חברות',
    'nav.analysts': 'אנליסטים',
    'nav.sectors': 'סקטורים',

    // Chat
    'chat.placeholder': 'שאל שאלה על המסמכים שלך...',
    'chat.send': 'שלח',
    'chat.newChat': 'שיחה חדשה',
    'chat.conversations': 'שיחות',
    'chat.noConversations': 'אין שיחות עדיין',
    'chat.thinking': 'חושב...',
    'chat.askQuestion': 'שאל שאלה...',
    'chat.clearHistory': 'נקה היסטוריה',
    'chat.exportChat': 'ייצא שיחה',

    // Sources
    'sources.title': 'מקורות',
    'sources.noSources': 'אין מקורות זמינים',
    'sources.viewDocument': 'צפה במסמך',
    'sources.page': 'עמוד',
    'sources.relevance': 'רלוונטיות',

    // Common
    'common.loading': 'טוען...',
    'common.error': 'שגיאה',
    'common.retry': 'נסה שנית',
    'common.cancel': 'ביטול',
    'common.save': 'שמור',
    'common.delete': 'מחק',
    'common.edit': 'ערוך',
    'common.close': 'סגור',
    'common.search': 'חיפוש',
    'common.filter': 'סינון',
    'common.export': 'ייצוא',
    'common.copy': 'העתק',
    'common.copied': 'הועתק!',
    'common.download': 'הורדה',
    'common.upload': 'העלאה',
    'common.refresh': 'רענן',
    'common.back': 'חזרה',
    'common.next': 'הבא',
    'common.previous': 'הקודם',
    'common.submit': 'שלח',
    'common.confirm': 'אשר',
    'common.yes': 'כן',
    'common.no': 'לא',
    'common.all': 'הכל',
    'common.none': 'ללא',
    'common.select': 'בחר',
    'common.clear': 'נקה',
    'common.reset': 'איפוס',
    'common.apply': 'החל',
    'common.noResults': 'לא נמצאו תוצאות',
    'common.success': 'הצלחה!',
    'common.failed': 'נכשל',
    'common.pending': 'ממתין',
    'common.processing': 'מעבד...',
    'common.complete': 'הושלם',
    'common.view': 'צפה',
    'common.hide': 'הסתר',
    'common.show': 'הצג',
    'common.more': 'עוד',
    'common.less': 'פחות',

    // User
    'user.profile': 'פרופיל',
    'user.logout': 'התנתק',
    'user.settings': 'הגדרות',
    'user.login': 'התחבר',
    'user.register': 'הרשמה',
    'user.email': 'אימייל',
    'user.password': 'סיסמה',
    'user.name': 'שם',

    // Documents
    'documents.title': 'מסמכים',
    'documents.upload': 'העלה מסמך',
    'documents.noDocuments': 'לא נמצאו מסמכים',
    'documents.searchPlaceholder': 'חפש מסמכים...',
    'documents.recentDocuments': 'מסמכים אחרונים',
    'documents.allDocuments': 'כל המסמכים',
    'documents.viewDocument': 'צפה במסמך',
    'documents.deleteDocument': 'מחק מסמך',
    'documents.downloadPdf': 'הורד PDF',

    // Document Viewer
    'viewer.zoomIn': 'הגדל',
    'viewer.zoomOut': 'הקטן',
    'viewer.fitWidth': 'התאם לרוחב',
    'viewer.fitPage': 'התאם לעמוד',
    'viewer.actualSize': 'גודל מקורי',
    'viewer.download': 'הורד PDF',
    'viewer.fullscreen': 'מסך מלא',
    'viewer.exitFullscreen': 'יציאה ממסך מלא',
    'viewer.askAI': 'שאל AI',
    'viewer.copyText': 'העתק טקסט',
    'viewer.copyImage': 'העתק תמונה',
    'viewer.exportExcel': 'ייצא לאקסל',
    'viewer.searchInDocument': 'חפש במסמך...',
    'viewer.pageOf': 'מתוך',
    'viewer.gridView': 'תצוגת רשת',
    'viewer.liquidView': 'תצוגת טקסט',
    'viewer.blockInspector': 'בודק בלוקים',
    'viewer.clickToInspect': 'לחץ על בלוק לבדיקה...',
    'viewer.tableExported': 'הטבלה יוצאה לאקסל',
    'viewer.textCopied': 'הטקסט הועתק',
    'viewer.imageCopied': 'התמונה הועתקה',
    'viewer.imageDownloaded': 'התמונה הורדה',

    // Research Hub
    'research.title': 'מרכז מחקר',
    'research.latestReports': 'דוחות אחרונים',
    'research.filterBySector': 'סנן לפי סקטור',
    'research.filterByCompany': 'סנן לפי חברה',
    'research.filterByAnalyst': 'סנן לפי אנליסט',
    'research.filterByDate': 'סנן לפי תאריך',
    'research.sortBy': 'מיין לפי',
    'research.dateNewest': 'תאריך (חדש ביותר)',
    'research.dateOldest': 'תאריך (ישן ביותר)',
    'research.relevance': 'רלוונטיות',
    'research.companyAZ': 'חברה (א-ת)',
    'research.askAboutResearch': 'שאל על מחקר...',
    'research.viewAll': 'צפה בהכל',

    // Portfolio
    'portfolio.title': 'תיק השקעות',
    'portfolio.holdings': 'אחזקות',
    'portfolio.watchlist': 'רשימת מעקב',
    'portfolio.performance': 'ביצועים',
    'portfolio.addHolding': 'הוסף אחזקה',
    'portfolio.removeHolding': 'הסר אחזקה',
    'portfolio.totalValue': 'שווי כולל',
    'portfolio.dayChange': 'שינוי יומי',
    'portfolio.allocation': 'הקצאה',

    // Table Headers
    'table.company': 'חברה',
    'table.ticker': 'סימול',
    'table.sector': 'סקטור',
    'table.date': 'תאריך',
    'table.analyst': 'אנליסט',
    'table.rating': 'דירוג',
    'table.price': 'מחיר',
    'table.target': 'יעד',
    'table.change': 'שינוי',
    'table.volume': 'מחזור',
    'table.marketCap': 'שווי שוק',
    'table.peRatio': 'מכפיל רווח',
    'table.dividend': 'דיבידנד',
    'table.title': 'כותרת',
    'table.type': 'סוג',
    'table.status': 'סטטוס',
    'table.actions': 'פעולות',
    'table.source': 'מקור',
    'table.pages': 'עמודים',

    // Settings
    'settings.title': 'הגדרות',
    'settings.language': 'שפה',
    'settings.theme': 'ערכת נושא',
    'settings.darkMode': 'מצב כהה',
    'settings.lightMode': 'מצב בהיר',
    'settings.notifications': 'התראות',
    'settings.emailNotifications': 'התראות אימייל',
    'settings.pushNotifications': 'התראות דחיפה',
    'settings.privacy': 'פרטיות',
    'settings.account': 'חשבון',
    'settings.changePassword': 'שנה סיסמה',
    'settings.deleteAccount': 'מחק חשבון',
    'settings.preferences': 'העדפות',
    'settings.display': 'תצוגה',
    'settings.general': 'כללי',

    // Admin
    'admin.title': 'פאנל ניהול',
    'admin.users': 'משתמשים',
    'admin.documents': 'מסמכים',
    'admin.analytics': 'אנליטיקס',
    'admin.systemStatus': 'סטטוס מערכת',
    'admin.logs': 'לוגים',

    // Ingestion
    'ingestion.title': 'קליטת מסמכים',
    'ingestion.uploadPdf': 'העלה PDF',
    'ingestion.processingStatus': 'סטטוס עיבוד',
    'ingestion.queued': 'בתור',
    'ingestion.processing': 'מעבד',
    'ingestion.completed': 'הושלם',
    'ingestion.failed': 'נכשל',
    'ingestion.reprocess': 'עבד מחדש',
    'ingestion.batchUpload': 'העלאה מרובה',

    // Actions
    'action.runScraper': 'הפעל סורק',
    'action.ask': 'שאל',
    'action.submit': 'שלח',
    'action.viewDetails': 'צפה בפרטים',
    'action.openInNewTab': 'פתח בלשונית חדשה',
    'action.share': 'שתף',
    'action.print': 'הדפס',
    'action.bookmark': 'סימניה',
    'action.addToWatchlist': 'הוסף לרשימת מעקב',
    'action.removeFromWatchlist': 'הסר מרשימת מעקב',

    // Messages
    'msg.welcome': 'ברוך הבא',
    'msg.confirmDelete': 'האם אתה בטוח שברצונך למחוק?',
    'msg.unsavedChanges': 'יש לך שינויים שלא נשמרו. האם אתה בטוח שברצונך לצאת?',
    'msg.sessionExpired': 'פג תוקף ההתחברות. אנא התחבר שנית.',
    'msg.networkError': 'שגיאת רשת. אנא בדוק את החיבור.',
    'msg.serverError': 'שגיאת שרת. אנא נסה שוב מאוחר יותר.',
    'msg.unauthorized': 'אין לך הרשאה לבצע פעולה זו.',
    'msg.notFound': 'הפריט המבוקש לא נמצא.',
    'msg.invalidInput': 'אנא בדוק את הקלט ונסה שנית.',
    'msg.saveSuccess': 'השינויים נשמרו בהצלחה.',
    'msg.deleteSuccess': 'הפריט נמחק בהצלחה.',
    'msg.uploadSuccess': 'הקובץ הועלה בהצלחה.',
    'msg.exportSuccess': 'הייצוא הושלם בהצלחה.',

    // Dates
    'date.today': 'היום',
    'date.yesterday': 'אתמול',
    'date.thisWeek': 'השבוע',
    'date.thisMonth': 'החודש',
    'date.thisYear': 'השנה',
    'date.custom': 'טווח מותאם',

    // Company Page
    'company.currentRating': 'דירוג נוכחי',
    'company.priceTarget': 'מחיר יעד',
    'company.currentPrice': 'מחיר נוכחי',
    'company.upside': 'פוטנציאל עלייה',
    'company.asOf': 'נכון ל',
    'company.noRating': 'אין דירוג זמין',
    'company.coveringAnalysts': 'אנליסטים מכסים',
    'company.noAnalysts': 'לא נמצאו אנליסטים',
    'company.valuation': 'מתודולוגיית הערכה',
    'company.ratingHistory': 'היסטוריית דירוגים',
    'company.prevRating': 'דירוג קודם',
    'company.prevTarget': 'יעד קודם',
    'company.recentDocuments': 'מסמכים אחרונים',
    'company.topics': 'נושאים מרכזיים',

    // AI Chat specific
    'ai.askAnything': 'שאל כל שאלה על המסמכים שלך',
    'ai.generating': 'מייצר תשובה...',
    'ai.sources': 'מקורות',
    'ai.confidence': 'רמת ביטחון',
    'ai.copyResponse': 'העתק תשובה',
    'ai.regenerate': 'צור מחדש',
    'ai.feedback': 'האם זה עזר?',
    'ai.thumbsUp': 'עזר',
    'ai.thumbsDown': 'לא עזר',

    // Progress Steps
    'progress.understanding': 'מבין',
    'progress.routing': 'מחפש מסמכים',
    'progress.retrieving': 'קורא בלוקים',
    'progress.generating': 'מייצר',

    // Source Grouping
    'sources.blocks': 'בלוקים',
    'sources.docs': 'מסמכים',
    'sources.jumpToCitation': 'קפוץ לציטוט',

    // Thinking Trail
    'thinking.researchContext': 'הקשר מחקרי',
    'thinking.plan': 'תוכנית',
    'thinking.evidence': 'ראיות',
    'thinking.themes': 'נושאים',
    'thinking.cost': 'עלות',

    // Changes View
    'companies.gridView': 'רשת',
    'companies.changesView': 'שינויים',
    'companies.upgrades': 'שדרוגים',
    'companies.downgrades': 'הורדות',
    'companies.initiations': 'התחלות כיסוי',
    'companies.noChanges': 'לא נמצאו שינויי דירוג',

    // Research Hub
    'research.timeRange': 'טווח זמן',
    'research.contentType': 'סוג תוכן',
    'research.askSarah': 'שאל את שרה במקום',
    'research.noMatch': 'אין מסמכים שתואמים את הסינון',

    // Viewer TOC
    'viewer.toc': 'תוכן עניינים',
    'viewer.noToc': 'אין תוכן עניינים זמין',

    // Summary Slider
    'summary.brief': 'קצר',
    'summary.standard': 'רגיל',
    'summary.detailed': 'מפורט',
    'summary.title': 'סיכום',

    // Compare Page
    'compare.title': 'השוואת מסמכים',
    'compare.needTwo': 'השוואה דורשת שני מסמכים',
    'compare.withPrevious': 'השווה עם הקודם',
  }
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [uiLanguage, setUiLanguageState] = useState<Language>(() => {
    const saved = localStorage.getItem('uiLanguage');
    return (saved === 'Hebrew' || saved === 'English') ? saved : 'English';
  });

  const setUiLanguage = useCallback((lang: Language) => {
    setUiLanguageState(lang);
    localStorage.setItem('uiLanguage', lang);
    document.documentElement.dir = lang === 'Hebrew' ? 'rtl' : 'ltr';
    document.documentElement.lang = lang === 'Hebrew' ? 'he' : 'en';
  }, []);

  const t = useCallback((key: string): string => {
    return translations[uiLanguage][key] || key;
  }, [uiLanguage]);

  // Set initial direction on mount
  useEffect(() => {
    document.documentElement.dir = uiLanguage === 'Hebrew' ? 'rtl' : 'ltr';
    document.documentElement.lang = uiLanguage === 'Hebrew' ? 'he' : 'en';
  }, [uiLanguage]);

  const value: LanguageContextType = {
    uiLanguage,
    setUiLanguage,
    t,
    isRTL: uiLanguage === 'Hebrew'
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Helper to detect query language (for chat auto-detection)
export const detectQueryLanguage = (text: string): Language => {
  const hebrewChars = (text.match(/[\u0590-\u05FF]/g) || []).length;
  return hebrewChars > text.length * 0.1 ? 'Hebrew' : 'English';
};

export default LanguageContext;
